<template>
    <div class="card">
        <div class="card-header">
            <h3 class="card-title">Scheduled Jobs</h3>
            <div class="card-tools">
                <div class="btn-group">
                    <button class="btn btn-sm btn-success" @click="$emit('create')">
                        <i class="fas fa-plus"></i> New Schedule
                    </button>
                    <button class="btn btn-sm btn-info" @click="refreshSchedules" :disabled="refreshing">
                        <i class="fas fa-sync" :class="{'fa-spin': refreshing}"></i> Refresh
                    </button>
                </div>
            </div>
        </div>

        <div class="card-body table-responsive p-0">
            <table class="table table-hover table-striped">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Operation</th>
                        <th>Target</th>
                        <th>Schedule</th>
                        <th>Last Run</th>
                        <th>Status</th>
                        <th class="text-center">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="job in jobs" :key="job.id">
                        <td>
                            <strong>{{ job.name }}</strong>
                            <br>
                            <small class="text-muted">{{ job.description }}</small>
                        </td>
                        <td>
                            <span class="badge" :class="operationBadgeClass(job.operation_type)">
                                {{ formatOperationType(job.operation_type) }}
                            </span>
                        </td>
                        <td>
                            <span class="badge badge-secondary">{{ job.target_type }}</span>
                            <template v-if="job.target_ids && job.target_ids.length > 0">
                                <span 
                                    v-for="(tid, idx) in job.target_ids.slice(0, 3)" 
                                    :key="tid" 
                                    class="badge badge-light ml-1"
                                    :title="job.target_names && job.target_names[idx] ? job.target_names[idx] : ''"
                                >
                                    #{{ tid }}
                                </span>
                                <span v-if="job.target_ids.length > 3" class="text-muted ml-1">
                                    +{{ job.target_ids.length - 3 }} more
                                </span>
                            </template>
                            <template v-else-if="job.target_id > 0">
                                <span class="badge badge-light ml-1">#{{ job.target_id }}</span>
                                <small v-if="job.target_name">({{ job.target_name }})</small>
                            </template>
                            <span v-else class="text-muted ml-1">All</span>
                        </td>
                        <td>
                            <span v-if="job.schedule_time">
                                <i class="far fa-clock"></i> {{ job.schedule_time }}
                            </span>
                            <span v-else-if="job.cron_expression">
                                <code>{{ job.cron_expression }}</code>
                            </span>
                            <span v-else class="text-muted">Manual only</span>
                            <br>
                            <small class="text-muted">{{ formatDateMode(job.date_mode) }}</small>
                        </td>
                        <td>
                            <span v-if="job.last_run">
                                {{ formatDate(job.last_run) }}
                                <br>
                                <span class="badge" :class="statusBadgeClass(job.last_run_status)">
                                    {{ job.last_run_status }}
                                </span>
                            </span>
                            <span v-else class="text-muted">Never</span>
                        </td>
                        <td>
                            <div class="custom-control custom-switch">
                                <input 
                                    type="checkbox" 
                                    class="custom-control-input" 
                                    :id="'toggle-' + job.id"
                                    :checked="job.enabled"
                                    @change="toggleJob(job)"
                                >
                                <label class="custom-control-label" :for="'toggle-' + job.id">
                                    {{ job.enabled ? 'Enabled' : 'Disabled' }}
                                </label>
                            </div>
                        </td>
                        <td class="text-center">
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-primary" @click="runJob(job)" :disabled="running[job.id]" title="Run Now">
                                    <i class="fas fa-play" :class="{'fa-spin': running[job.id]}"></i>
                                </button>
                                <button class="btn btn-default" @click="$emit('edit', job)" title="Edit">
                                    <i class="fas fa-edit"></i>
                                </button>
                                <button class="btn btn-danger" @click="deleteJob(job)" title="Delete">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="jobs.length === 0 && isLoaded">
                        <td colspan="7" class="text-center text-muted py-4">
                            <i class="fas fa-calendar-times fa-3x mb-3"></i>
                            <p>No scheduled jobs found. Create one to get started.</p>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="overlay" v-if="!isLoaded">
            <point-spinner />
        </div>
    </div>
</template>

<script>
import { RepositoryFactory } from '@/services/repositories/ManagementAPI/RepositoryFactory'
const SchedulerRepo = RepositoryFactory.get('scheduler')

export default {
    name: 'SchedulerList',
    props: {
        jobs: {
            type: Array,
            required: false,
            default: () => []
        },
        isLoaded: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            running: {},
            refreshing: false
        }
    },
    methods: {
        formatOperationType(type) {
            return type === 'request-statistics' ? 'Request Stats' : 'Recalculate'
        },
        operationBadgeClass(type) {
            return type === 'request-statistics' ? 'badge-info' : 'badge-warning'
        },
        formatDateMode(mode) {
            const modes = {
                'today': 'Today\'s data',
                'yesterday': 'Yesterday\'s data',
                'hourly': 'Hourly updates',
                'specific': 'Specific date',
                'range': 'Date range'
            }
            return modes[mode] || mode
        },
        statusBadgeClass(status) {
            const classes = {
                'success': 'badge-success',
                'failed': 'badge-danger',
                'error': 'badge-danger',
                'running': 'badge-info'
            }
            return classes[status] || 'badge-secondary'
        },
        formatDate(dateStr) {
            if (!dateStr) return ''
            const date = new Date(dateStr)
            return date.toLocaleString()
        },
        async toggleJob(job) {
            try {
                await SchedulerRepo.toggle(job.id)
                this.$emit('refresh')
                this.$toast.success(`Schedule ${job.enabled ? 'disabled' : 'enabled'}`)
            } catch (error) {
                this.$toast.error('Failed to toggle schedule')
                console.error(error)
            }
        },
        async runJob(job) {
            this.$set(this.running, job.id, true)
            try {
                const response = await SchedulerRepo.run(job.id)
                this.$toast.success(`Job executed: ${response.data.message}`)
                this.$emit('refresh')
            } catch (error) {
                this.$toast.error(error.response?.data?.detail?.message || 'Failed to run job')
                console.error(error)
            } finally {
                this.$set(this.running, job.id, false)
            }
        },
        async deleteJob(job) {
            if (!confirm(`Are you sure you want to delete "${job.name}"?`)) {
                return
            }
            try {
                await SchedulerRepo.delete(job.id)
                this.$emit('refresh')
                this.$toast.success('Schedule deleted')
            } catch (error) {
                this.$toast.error('Failed to delete schedule')
                console.error(error)
            }
        },
        async refreshSchedules() {
            this.refreshing = true
            try {
                await SchedulerRepo.refresh()
                this.$emit('refresh')
                this.$toast.success('Schedules refreshed')
            } catch (error) {
                this.$toast.error('Failed to refresh schedules')
                console.error(error)
            } finally {
                this.refreshing = false
            }
        }
    }
}
</script>

<style scoped>
.badge {
    font-size: 0.8em;
}
.custom-switch {
    padding-left: 2.5rem;
}
</style>
